/* oxlint-disable react/static-components -- these render APIs intentionally receive component references */
import { Calendar as CalendarIcon, Check } from 'iconoir-react'
import { compact } from 'lodash'
import { Fragment } from 'react'
import { ActivityCard } from 'app/components/activities/ActivityCard'
import { KeyValueDrawer } from 'app/components/activities/KeyValueDrawer'
import { Badge, ColorStyles } from 'app/components/Badge'
import { LazyKollaProvider } from 'app/components/shared/LazyKollaProvider'
import { LazySikkaProvider } from 'app/components/shared/LazySikkaProvider'
import Spinner from 'app/components/Spinner'
import { TreatmentCategoryBadge } from 'app/components/TreatmentCategoryBadge'
import { appointmentTimestamp, formatDate } from 'app/date.utils'
import { useCdtCodeMappings } from 'app/hooks/useCdtCodeMappings'
import { useLocationTimezone } from 'app/hooks/useLocationTimezone'
import type { AppointmentJson } from 'app/types/entities'
import { maskSensitiveData } from 'app/sanitize.utils'
import { getTreatmentCategories } from 'lib/cdtCodeDefaults'
import { isAppointmentConfirmed } from 'lib/appointmentConfirmed'
import { formatCurrency } from 'lib/formatCurrency'

/** Status colour in both places it appears, so they cannot drift apart. */
const STATUS_STYLES: Record<string, { badge: ColorStyles; icon: string }> = {
  /*
   * Orange, not red, and the same orange the contact's own "Broken
   * Appointment" tag uses. A patient who broke an appointment reads as one
   * thing on the contact and another in the feed otherwise, and red in this
   * palette is reserved for failure rather than for a booking that moved.
   */
  Broken: { badge: `orange`, icon: `bg-orange-500` },
  Completed: { badge: `green`, icon: `bg-green-600` },
  Scheduled: { badge: `blue`, icon: `bg-blue-600` },
}

function AiAgentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g opacity="0.8">
        <path
          d="M6 0.999805C6 0.999805 6.66547 3.28225 7.69141 4.3084C8.71409 5.33108 10.9851 5.99545 11 5.9998C11 5.9998 8.71746 6.66614 7.69141 7.69219C6.67353 8.71032 6.01033 10.9646 6 10.9998C5.99 10.9657 5.32589 8.71046 4.30762 7.69219C3.28147 6.66625 1 5.9998 1 5.9998C1.02212 5.99333 3.28647 5.32929 4.30762 4.3084C5.33367 3.28235 6 0.999805 6 0.999805Z"
          fill="url(#ai-agent-gradient)"
        />
      </g>
      <defs>
        <linearGradient
          id="ai-agent-gradient"
          x1="9.22848"
          y1="0.999805"
          x2="1.63976"
          y2="10.9242"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#67C3FF" />
          <stop offset="0.600962" stopColor="#2A40F5" />
        </linearGradient>
      </defs>
    </svg>
  )
}

type Props = {
  appointment: AppointmentJson
  isFirst?: boolean
  isLast?: boolean
  alwaysOpen?: boolean
}

export function AppointmentCard({ appointment, isFirst, isLast, alwaysOpen }: Props) {
  const { mappings: customMappings, loading: mappingsLoading } = useCdtCodeMappings()
  const hasProcedureCodes = (appointment.procedureCodes || []).length > 0
  const isDeletedFromPms = appointment.pmsStatus === `Deleted`
  const timezone = useLocationTimezone(appointment.locationId)
  const { value: scheduledForValue, ...scheduledForOptions } = appointmentTimestamp({
    date: appointment.date,
    time: appointment.time,
    timezone,
  })

  // oxlint-disable-next-line react/no-unstable-nested-components -- render helper intentionally captures parent state
  const Title = () => {
    return (
      <div className="flex flex-wrap items-center gap-x-1 text-ui-sm text-strong">
        <span>{appointment?.type || `Appointment`}</span>
        {appointment?.sikkaOfficeId && (
          <LazySikkaProvider
            sikkaOfficeId={appointment.sikkaOfficeId}
            sikkaPracticeId={appointment.sikkaPracticeId}
            providerId={appointment.providerId}
            showIcon
            showSpinner
          />
        )}
        {appointment?.locationId && appointment?.providerId && (
          <LazyKollaProvider locationId={appointment?.locationId} providerId={appointment?.providerId} showSpinner />
        )}
      </div>
    )
  }

  /*
   * Always a calendar. Swapping the glyph per status meant the one thing every
   * row on the feed shares - that it is an appointment - was the one thing the
   * icon stopped saying, while status was already stated twice over in the
   * badge beside the title. Colour carries the status; shape carries the kind.
   */
  const getAppointmentIcon = () => (
    <CalendarIcon
      className={`h-5 w-5 rounded-md p-1 text-on-color ${STATUS_STYLES[appointment.status ?? ``]?.icon ?? `bg-fill-neutral-strong`}`}
    />
  )

  const getAppointmentStatus = () => {
    if (!appointment.status) return null
    return (
      <Fragment>
        <Badge color={STATUS_STYLES[appointment.status]?.badge} label={appointment.status} />
        {isAppointmentConfirmed(appointment) && <Badge color="green" icon={<Check />} label="Confirmed" />}
        {isDeletedFromPms && (
          <span className="rounded border bg-red-100 px-1.5 text-xs font-medium not-italic leading-4 text-red-700">
            Deleted
          </span>
        )}
      </Fragment>
    )
  }

  const getAppointmentSource = () => {
    if (!appointment.source) return null

    if (appointment.source === `Scheduler`) {
      return (
        <span className="inline-flex h-5 items-center gap-0.5 rounded border border-default bg-surface-muted px-2 py-[5px] text-xs font-medium leading-[1.25] text-default">
          <CalendarIcon className="h-3 w-3 shrink-0" />
          <span>Online Scheduler</span>
        </span>
      )
    }

    // Voice Agent or SMS Agent - use gradient design
    return (
      <span
        className="inline-flex h-5 items-center gap-0.5 rounded border border-default px-2 py-[5px] text-xs font-medium leading-[1.25] text-blue-700"
        style={{
          backgroundImage: `linear-gradient(189deg, rgba(103, 195, 255, 0.1) 7.68%, rgba(42, 64, 245, 0.1) 61.24%)`,
        }}
      >
        <AiAgentIcon className="h-3 w-3 shrink-0" />
        <span>{appointment.source}</span>
      </span>
    )
  }

  const getAppointmentContent = (isPreview?: boolean) => (
    <div className="w-full items-center rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm">
      <KeyValueDrawer
        map={{
          Amount: formatCurrency(appointment.amount ?? 0, 2),
          'Procedure Codes':
            compact([
              ...new Set([
                appointment.procedureCode1,
                appointment.procedureCode2,
                appointment.procedureCode3,
                ...(appointment.procedureCodes || []),
              ]),
            ]).join(`, `) || `-`,
          'Appointment made date': formatDate({ value: appointment.appointmentMadeDate }),
        }}
      />
      {!isPreview && (
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2 mt-3 flex grow flex-col">
            <span className="text-body-xs text-subtle">Note</span>
            <div className="mt-0.5 text-ui-xs text-strong">{maskSensitiveData(appointment.note) || `-`}</div>
          </div>
          <div className="mt-3 flex w-[160px] grow-0 flex-col">
            <span className="text-body-xs text-subtle">Date / Time</span>
            <div className="mt-0.5 text-ui-xs text-strong">
              {formatDate({ value: scheduledForValue, ...scheduledForOptions }) || `-`}
            </div>
          </div>
        </div>
      )}
    </div>
  )

  return (
    <ActivityCard
      title={<Title />}
      icon={getAppointmentIcon()}
      activityDate={scheduledForValue}
      activityDateOptions={scheduledForOptions}
      tags={
        <Fragment>
          {getAppointmentSource()}
          {getAppointmentStatus()}
          {hasProcedureCodes && mappingsLoading ? (
            <Spinner size={4} />
          ) : (
            getTreatmentCategories(appointment.procedureCodes || [], customMappings).map((category) => (
              <TreatmentCategoryBadge key={category} category={category} />
            ))
          )}
        </Fragment>
      }
      preview={getAppointmentContent(true)}
      drawer={getAppointmentContent(false)}
      isFirst={isFirst}
      isLast={isLast}
      alwaysOpen={alwaysOpen}
    />
  )
}
